// The case detail's right-hand workflow sidebar (coo-lab parity): the status pill, the legal
// stage transitions for this discipline, and staff assignment. Transitions come from the shared
// allowedTransitions() so the UI can never offer an illegal move; the linear next-stage is offered
// as "Advance" (the /advance endpoint) and the doctor gate as "Approve setup", while everything
// else (hold/cancel/QC-fail/resume) posts the generic /status endpoint. All writes go through the
// offline queue and rely on the case.updated SSE (via onChanged) to reconcile.
import { useEffect, useState } from 'react';
import {
  type Case,
  type CaseStatus,
  type AdminUser,
  allowedTransitions,
  nextStage,
  CASE_STATUS_LABELS,
} from '@alignova/shared';
import { api } from '../../lib/api.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { Button, Card, Pill } from '../../ui/primitives.tsx';

function statusTone(status: CaseStatus): 'neutral' | 'good' | 'bad' {
  if (status === 'delivered') return 'good';
  if (status === 'cancelled' || status === 'remake') return 'bad';
  return 'neutral';
}

export function CaseWorkflowPanel({
  c,
  onChanged,
  onDeleted,
}: {
  c: Case;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const can = useCan();
  const canAssign = can('user:manage') && can('case:update');
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The assignment dropdown needs the staff list, which is admin-gated; skip the fetch otherwise.
  useEffect(() => {
    if (!canAssign) return;
    let cancelled = false;
    api
      .get<AdminUser[]>('/users?limit=200')
      .then((list) => {
        if (!cancelled) setUsers(list.filter((u) => u.isActive));
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canAssign]);

  const atGate = c.status === 'approval';
  const linearNext = nextStage(c.caseType, c.status);
  const canAdvance = linearNext !== null && !atGate && can('case:advance_stage');

  // Everything except the linear next (covered by Advance) and the approval→production gate move
  // (which the generic /status endpoint rejects — it goes through Approve setup instead).
  const transitions = allowedTransitions(c.caseType, c.status).filter((s) => {
    if (canAdvance && s === linearNext) return false;
    if (atGate && s === 'production') return false;
    return true;
  });

  async function run(
    fn: () => Promise<unknown>,
    errKey: 'cases.detail.statusError' | 'cases.detail.assignError' | 'cases.detail.revisionError',
  ) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      onChanged();
    } catch {
      setError(t(errKey));
    } finally {
      setBusy(false);
    }
  }

  const changeStatus = (status: CaseStatus) =>
    run(() => api.post(`/cases/${c.id}/status`, { status }, { queue: true }), 'cases.detail.statusError');
  const advance = () =>
    run(() => api.post(`/cases/${c.id}/advance`, {}, { queue: true }), 'cases.detail.statusError');
  const approve = () =>
    run(() => api.post(`/cases/${c.id}/approve-setup`, {}, { queue: true }), 'cases.detail.statusError');
  const assign = (userId: string) =>
    run(() => api.patch(`/cases/${c.id}`, { assignedTo: userId || null }), 'cases.detail.assignError');

  function requestRevision() {
    const note = window.prompt(t('cases.detail.requestRevision'));
    if (note == null || !note.trim()) return;
    run(() => api.post(`/cases/${c.id}/request-revision`, { note: note.trim() }), 'cases.detail.revisionError');
  }

  async function deleteCase() {
    if (!window.confirm(t('cases.detail.deleteConfirm'))) return;
    setError(null);
    setBusy(true);
    try {
      await api.del(`/cases/${c.id}`);
      onDeleted();
    } catch {
      setError(t('cases.detail.deleteError'));
      setBusy(false);
    }
  }

  // Hold/cancel/remake read as caution; forward moves are primary.
  const isCaution = (s: CaseStatus) => s === 'on_hold' || s === 'cancelled' || s === 'remake';

  return (
    <>
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold text-ink">{t('cases.detail.workflow')}</h3>
        <div className="mb-3">
          <Pill tone={statusTone(c.status)}>{CASE_STATUS_LABELS[c.status]}</Pill>
        </div>
        {error && <p className="mb-2 text-xs text-status-urgent-fg">{error}</p>}
        <div className="flex flex-col gap-2">
          {atGate && can('case:approve_setup') && (
            <>
              <Button disabled={busy} onClick={approve}>
                {t('cases.approve')}
              </Button>
              <Button
                variant="ghost"
                disabled={busy}
                className="text-status-urgent-fg"
                onClick={requestRevision}
              >
                {t('cases.detail.requestRevision')}
              </Button>
            </>
          )}
          {canAdvance && (
            <Button disabled={busy} onClick={advance}>
              {c.status === 'qc' ? t('cases.detail.qcPass') : `${t('cases.advance')} → ${CASE_STATUS_LABELS[linearNext!]}`}
            </Button>
          )}
          {can('case:advance_stage') &&
            transitions.map((s) => (
              <Button
                key={s}
                variant="ghost"
                disabled={busy}
                className={isCaution(s) ? 'text-status-urgent-fg' : ''}
                onClick={() => changeStatus(s)}
              >
                → {CASE_STATUS_LABELS[s]}
              </Button>
            ))}
        </div>
      </Card>

      {canAssign && (
        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold text-ink">{t('cases.detail.assignedStaff')}</h3>
          <select
            className="w-full rounded border border-line bg-surface px-3 py-2 text-sm"
            value={c.assignedTo ?? ''}
            disabled={busy || users === null}
            onChange={(e) => assign(e.target.value)}
          >
            <option value="">{t('cases.detail.assignUnassigned')}</option>
            {(users ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </Card>
      )}

      {can('case:delete') && (
        <Card className="p-4">
          <Button
            variant="ghost"
            disabled={busy}
            className="w-full text-status-urgent-fg"
            onClick={deleteCase}
          >
            {t('cases.detail.delete')}
          </Button>
        </Card>
      )}
    </>
  );
}
